import { Calendar, type LocaleInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';

export type Configuration = {
  api?: {
    url: string;
    method?: string;
    bodyParams?: Record<string, string>;
  };
  locale?: {
    code: string;
    today?: string;
    month?: string;
    week?: string;
    weekShort?: string;
    allDay?: string;
    switchToWeek?: string;
    switchToToday?: string;
    switchToMonth?: string;
    nextWeek?: string;
    prevWeek?: string;
    nextMonth?: string;
    prevMonth?: string;
  };
};

const useCalendar = ($calendar: HTMLElement, configuration: Configuration): Calendar => {
  const calendar = new Calendar($calendar, {
    plugins: [dayGridPlugin, timeGridPlugin],
    initialView: 'dayGridMonth',
    locale: {
      code: configuration.locale?.code ?? 'en',
      week: {
        dow: 1,
        doy: 4,
      },
      buttonText: {
        today: configuration.locale?.today,
        week: configuration.locale?.week,
        month: configuration.locale?.month,
      },
      weekText: configuration.locale?.weekShort,
      weekTextLong: configuration.locale?.week,
      allDayText: configuration.locale?.allDay,
      viewHint(_, view: string) {
        if (view === 'dayGridMonth') {
          return configuration.locale?.switchToMonth;
        }

        if (view === 'timeGridWeek') {
          return configuration.locale?.switchToWeek;
        }

        return undefined;
      },
      buttonHints: {
        prev(_, view: string) {
          if (view === 'month') {
            return configuration.locale?.prevMonth;
          }

          if (view === 'week') {
            return configuration.locale?.prevWeek;
          }

          throw new Error();
        },
        next(_, view: string) {
          if (view === 'month') {
            return configuration.locale?.nextMonth;
          }

          if (view === 'week') {
            return configuration.locale?.nextWeek;
          }

          throw new Error();
        },
        today: configuration.locale?.switchToToday,
      },
    } as LocaleInput,
    timeZone: 'Europe/Berlin',
    nowIndicator: true,
    weekNumbers: true,
    dayMaxEventRows: true,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek',
    },
    views: {
      timeGrid: {
        dayMaxEventRows: 6,
      },
    },
    eventClick(event) {
      if (event.event.url) {
        event.jsEvent.preventDefault();
        window.open(event.event.url, '_blank');
      }
    },
    events: async ({ start, end }, callback) => {
      if (configuration.api) {
        if (configuration.api.method?.toLowerCase() === 'post') {
          const body = new URLSearchParams(
            Object.assign(configuration.api.bodyParams ?? {}, {
              start: start.toISOString(),
              end: end.toISOString(),
            }),
          );

          const response = await fetch(configuration.api.url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
            },
            body,
          });

          callback(await response.json());
        } else {
          const response = await fetch(
            configuration.api.url.replace('__START__', start.toISOString()).replace('__END__', end.toISOString()),
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
              },
            },
          );

          callback(await response.json());
        }
      } else {
        callback([]);
      }
    },
  });

  calendar.render();

  return calendar;
};

export default useCalendar;
